"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Toast } from "@base-ui/react/toast";
import react from "react";
import { cn } from "../../utils/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { IconButton } from "../IconButton/index.js";
import { PlainButton } from "../PlainButton/index.js";
import { Span } from "../Text/index.js";
import { toastVariants } from "./Toast.variants.js";
const defaultIcons = {
    info: /*#__PURE__*/ jsx(CdnIcon, {
        iconName: "info"
    }),
    success: /*#__PURE__*/ jsx(CdnIcon, {
        iconName: "check_circle"
    }),
    caution: /*#__PURE__*/ jsx(CdnIcon, {
        iconName: "warning"
    }),
    error: /*#__PURE__*/ jsx(CdnIcon, {
        iconName: "error"
    })
};
const ToastPositionContext = /*#__PURE__*/ react.createContext('bottomRight');
function ToastProvider({ children, timeout = 10000, ...props }) {
    return /*#__PURE__*/ jsx(Toast.Provider, {
        timeout: timeout,
        ...props,
        children: children
    });
}
function ToastViewport({ position = 'bottomRight', className, slots: slotOverrides }) {
    const { viewportSlot } = toastVariants({
        position
    });
    const viewportClasses = cn(viewportSlot(), slotOverrides?.viewportSlot, className);
    return /*#__PURE__*/ jsx(ToastPositionContext.Provider, {
        value: position,
        children: /*#__PURE__*/ jsx(Toast.Portal, {
            children: /*#__PURE__*/ jsx(Toast.Viewport, {
                className: viewportClasses,
                children: /*#__PURE__*/ jsx(ToastList, {})
            })
        })
    });
}
function ToastList() {
    const { toasts } = Toast.useToastManager();
    return toasts.map((toast)=>/*#__PURE__*/ jsx(DefaultToast, {
            toast: toast
        }, toast.id));
}
function AnchoredToastViewport({ side = 'bottom', align = 'start', className }) {
    const position = 'bottom' === side ? 'top' : 'bottom';
    const { anchoredViewportSlot } = toastVariants({
        side,
        align
    });
    return /*#__PURE__*/ jsx(ToastPositionContext.Provider, {
        value: position,
        children: /*#__PURE__*/ jsx(Toast.Viewport, {
            className: cn(anchoredViewportSlot(), className),
            children: /*#__PURE__*/ jsx(ToastList, {})
        })
    });
}
function DefaultToast({ toast: toastObj }) {
    const data = toastObj.data ?? {};
    const palette = data.palette ?? 'neutral';
    const emphasis = data.emphasis ?? 'medium';
    const { bodySlot } = toastVariants({
        palette,
        emphasis
    });
    return /*#__PURE__*/ jsxs(ToastRoot, {
        toast: toastObj,
        palette: palette,
        emphasis: emphasis,
        children: [
            data.contentNode ? /*#__PURE__*/ jsx(ToastContent, {
                children: data.contentNode
            }) : /*#__PURE__*/ jsxs(ToastContent, {
                children: [
                    /*#__PURE__*/ jsx(ToastIcon, {
                        palette: palette,
                        emphasis: emphasis,
                        iconNode: data.iconNode,
                        hideIcon: data.hideIcon
                    }),
                    (toastObj.title || toastObj.description) && /*#__PURE__*/ jsxs("div", {
                        className: bodySlot(),
                        children: [
                            toastObj.title && /*#__PURE__*/ jsx(ToastTitle, {}),
                            toastObj.description && /*#__PURE__*/ jsx(ToastDescription, {})
                        ]
                    }),
                    toastObj.actionProps && /*#__PURE__*/ jsx(ToastAction, {
                        emphasis: emphasis
                    })
                ]
            }),
            !data.hideCloseButton && /*#__PURE__*/ jsx(ToastClose, {
                palette: palette,
                emphasis: emphasis
            })
        ]
    });
}
function ToastRoot({ palette = 'neutral', emphasis = 'medium', className, slots: slotOverrides, ...props }) {
    const position = react.useContext(ToastPositionContext);
    const { rootSlot } = toastVariants({
        palette,
        emphasis,
        position
    });
    const rootClasses = cn(rootSlot(), slotOverrides?.rootSlot, className);
    return /*#__PURE__*/ jsx(Toast.Root, {
        className: rootClasses,
        ...props
    });
}
function ToastContent({ className, slots: slotOverrides, ...props }) {
    const { contentSlot } = toastVariants({});
    const contentClasses = cn(contentSlot(), slotOverrides?.contentSlot, className);
    return /*#__PURE__*/ jsx(Toast.Content, {
        className: contentClasses,
        ...props
    });
}
function ToastIcon({ palette = 'neutral', emphasis = 'medium', iconNode, hideIcon = false, className, slots: slotOverrides }) {
    const { iconSlot } = toastVariants({
        palette,
        emphasis
    });
    if (hideIcon) return null;
    const icon = iconNode || defaultIcons[palette ?? 'neutral'];
    if (!icon) return null;
    const iconClasses = cn(iconSlot(), slotOverrides?.iconSlot, className);
    return /*#__PURE__*/ jsx("div", {
        className: iconClasses,
        children: icon
    });
}
function ToastTitle({ className, ...props }) {
    return /*#__PURE__*/ jsx(Toast.Title, {
        render: /*#__PURE__*/ jsx(Span, {
            bold: true,
            textStyle: "body2",
            className: className
        }),
        ...props
    });
}
function ToastDescription({ className, ...props }) {
    return /*#__PURE__*/ jsx(Toast.Description, {
        render: /*#__PURE__*/ jsx(Span, {
            textStyle: "body2",
            className: className
        }),
        ...props
    });
}
function ToastAction({ emphasis = 'medium', className, ...props }) {
    const { actionSlot } = toastVariants({});
    return /*#__PURE__*/ jsx(Toast.Action, {
        render: ({ children: actionChildren, ...renderProps })=>/*#__PURE__*/ jsx(PlainButton, {
                ...renderProps,
                type: "primary",
                textStyle: "body3",
                emphasis: emphasis,
                className: cn(actionSlot(), className),
                children: actionChildren
            }),
        ...props
    });
}
function ToastClose({ palette, emphasis, className, ...props }) {
    const { closeSlot } = toastVariants({});
    return /*#__PURE__*/ jsx(Toast.Close, {
        render: (renderProps)=>/*#__PURE__*/ jsx("div", {
                className: cn(closeSlot(), className),
                children: /*#__PURE__*/ jsx(IconButton, {
                    ...renderProps,
                    iconName: "close",
                    type: "plainSecondary",
                    size: "sm",
                    emphasis: 'bold' === emphasis && 'caution' !== palette ? 'bold' : void 0,
                    "aria-label": "Close toast"
                })
            }),
        ...props
    });
}
function ToastPositioner(props) {
    return /*#__PURE__*/ jsx(Toast.Positioner, {
        ...props
    });
}
function ToastArrow(props) {
    return /*#__PURE__*/ jsx(Toast.Arrow, {
        ...props
    });
}
export { AnchoredToastViewport, ToastAction, ToastArrow, ToastClose, ToastContent, ToastDescription, ToastIcon, ToastPositioner, ToastProvider, ToastRoot, ToastTitle, ToastViewport };
